import { create } from 'zustand'
import {
  getAnalytics,
  isSupported as isAnalyticsSupported,
  logEvent as fbLogEvent,
  setUserId as fbSetUserId,
  setUserProperties as fbSetUserProperties,
  type Analytics
} from 'firebase/analytics'
import { app, functions } from '@/config/firebase'
import { httpsCallable } from 'firebase/functions'

/**
 * Port of `src/stores/analytics.js` (web path only).
 *
 * The legacy native branch (`@capacitor-firebase/analytics`) is dropped. Web
 * uses `firebase/analytics` (GA4) when a measurement id is configured, plus
 * the first-party `recordSitePageView` callable on every screen view.
 */

const SITE_SESSION_KEY = 'naha_site_analytics_sid'

function firstPartyWebAnalyticsAllowed () {
  if (import.meta.env.PROD) return true
  return import.meta.env.VITE_ENABLE_WEB_ANALYTICS === 'true'
}

function getSiteSessionId () {
  if (typeof window === 'undefined') return null
  try {
    let id = window.sessionStorage.getItem(SITE_SESSION_KEY)
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `s${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64)
      if (id.length < 8) id = `${id}${Date.now()}`
      window.sessionStorage.setItem(SITE_SESSION_KEY, id)
    }
    return id
  } catch {
    return null
  }
}

const recordSitePageView = async (routeName, pagePath) => {
  if (!firstPartyWebAnalyticsAllowed()) return
  const sessionId = getSiteSessionId()
  if (!sessionId) return
  try {
    const fn = httpsCallable(functions, 'recordSitePageView')
    await fn({
      routeName: String(routeName || '').slice(0, 80),
      path: String(pagePath || '').slice(0, 160),
      sessionId
    })
  } catch {
    // First-party analytics must never break navigation.
  }
}

/** Prod builds always; local only when VITE_ENABLE_WEB_ANALYTICS=true */
function webAnalyticsAllowed () {
  const measurementId = String(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '').trim()
  if (!measurementId) return false
  if (import.meta.env.PROD) return true
  return import.meta.env.VITE_ENABLE_WEB_ANALYTICS === 'true'
}

let webAnalytics: Analytics | null = null
let initPromise: Promise<boolean> | null = null

export interface AnalyticsState {
  isInitialized: boolean
  currentUserId: string | null
  initialize: () => Promise<boolean>
  setUserId: (userId: string | null) => Promise<void>
  setUserProperties: (properties: Record<string, any>) => Promise<void>
  logEvent: (name: string, params?: Record<string, any>) => Promise<void>
  logScreenView: (screenName: string, screenClass?: string | null, pagePath?: string | null) => Promise<void>
  trackCheckIn: (cityId: string, isRegular: boolean, paymentMethod?: string | null) => Promise<void>
  trackCheckOut: (cityId: string) => Promise<void>
  trackLogin: (method?: string) => Promise<void>
  trackSignUp: (method?: string) => Promise<void>
  trackPurchase: (passType: string, price: number, currency?: string) => Promise<void>
  trackWaitlistPromotion: (cityId: string, gameDate: string) => Promise<void>
  trackNotificationToggle: (enabled: boolean) => Promise<void>
  trackCitySelect: (cityId: string) => Promise<void>
}

export const useAnalyticsStore = create<AnalyticsState>()((set, get) => {
  /**
   * Initialize analytics
   * Call this on app startup
   */
  const initialize = async () => {
    if (get().isInitialized) return true
    if (initPromise) return initPromise

    initPromise = (async () => {
      try {
        if (!webAnalyticsAllowed()) {
          return false
        }

        const supported = await isAnalyticsSupported()
        if (!supported) {
          return false
        }

        webAnalytics = getAnalytics(app)
        set({ isInitialized: true })
        return true
      } catch (error) {
        console.error('Error initializing analytics:', error)
        return false
      } finally {
        initPromise = null
      }
    })()

    return initPromise
  }

  const ensureInitialized = async () => {
    if (get().isInitialized) return true
    return initialize()
  }

  /**
   * Set the current user ID for analytics
   */
  const setUserId = async (userId) => {
    if (!(await ensureInitialized())) return

    try {
      if (!webAnalytics) return
      fbSetUserId(webAnalytics, userId || null)
      set({ currentUserId: userId || null })
      fbSetUserProperties(webAnalytics, {
        logged_in: userId ? 'true' : 'false'
      })
    } catch (error) {
      console.error('Error setting user ID:', error)
    }
  }

  /**
   * Set user properties for better segmentation
   */
  const setUserProperties = async (properties) => {
    if (!(await ensureInitialized())) return

    try {
      if (!webAnalytics) return
      const normalized = {}
      for (const [key, value] of Object.entries(properties)) {
        normalized[key] = String(value)
      }
      fbSetUserProperties(webAnalytics, normalized)
    } catch (error) {
      console.error('Error setting user properties:', error)
    }
  }

  /**
   * Log a custom event
   */
  const logEvent = async (name, params = {}) => {
    if (!(await ensureInitialized())) return

    try {
      if (!webAnalytics) return
      fbLogEvent(webAnalytics, name, params)
    } catch (error) {
      console.error('Error logging event:', error)
    }
  }

  /**
   * Log screen / page view (web GA4 page_view + first-party page view)
   */
  const logScreenView = async (screenName: string, screenClass: string | null = null, pagePath: string | null = null) => {
    const path = pagePath || (typeof window !== 'undefined' ? window.location.pathname : screenName)
    void recordSitePageView(screenName, path)

    if (!(await ensureInitialized())) return

    try {
      if (!webAnalytics) return
      fbLogEvent(webAnalytics, 'page_view', {
        page_title: screenName,
        page_path: path,
        page_location: typeof window !== 'undefined' ? window.location.href : undefined
      })
      fbLogEvent(webAnalytics, 'screen_view', {
        firebase_screen: screenName,
        firebase_screen_class: screenClass || screenName
      })
    } catch (error) {
      console.error('Error logging screen view:', error)
    }
  }

  // Pre-defined event helpers for common actions

  /**
   * Track when a user checks in
   */
  const trackCheckIn = async (cityId, isRegular, paymentMethod) => {
    await logEvent('check_in', {
      city_id: cityId,
      is_regular: isRegular ? 'true' : 'false',
      payment_method: paymentMethod || 'unknown'
    })
  }

  /**
   * Track when a user checks out
   */
  const trackCheckOut = async (cityId) => {
    await logEvent('check_out', {
      city_id: cityId
    })
  }

  /**
   * Track when a user logs in
   */
  const trackLogin = async (method = 'email') => {
    await logEvent('login', {
      method
    })
  }

  /**
   * Track when a user signs up
   */
  const trackSignUp = async (method = 'email') => {
    await logEvent('sign_up', {
      method
    })
  }

  /**
   * Track when a user purchases a pass
   */
  const trackPurchase = async (passType, price, currency = 'CAD') => {
    await logEvent('purchase', {
      item_name: passType,
      value: price,
      currency
    })
  }

  /**
   * Track when a user is moved from waitlist to team
   */
  const trackWaitlistPromotion = async (cityId, gameDate) => {
    await logEvent('waitlist_promotion', {
      city_id: cityId,
      game_date: gameDate
    })
  }

  /**
   * Track when a user enables/disables notifications
   */
  const trackNotificationToggle = async (enabled) => {
    await logEvent('notification_toggle', {
      enabled: enabled ? 'true' : 'false'
    })
  }

  /**
   * Track city selection/switch
   */
  const trackCitySelect = async (cityId) => {
    await logEvent('city_select', {
      city_id: cityId
    })
  }

  return {
    isInitialized: false,
    currentUserId: null,
    initialize,
    setUserId,
    setUserProperties,
    logEvent,
    logScreenView,
    trackCheckIn,
    trackCheckOut,
    trackLogin,
    trackSignUp,
    trackPurchase,
    trackWaitlistPromotion,
    trackNotificationToggle,
    trackCitySelect
  }
})
